import React, { useMemo, useRef, useEffect } from 'react';
import { Calendar } from 'lucide-react';
import { format } from 'date-fns';
import { DS, GS, hairline } from '../../lib/designTokens';
import { isGameLive, abbreviateOpponent } from '../../lib/game-utils';

export const GameStrip = ({ team, games, selectedGameId, onSelect, onCalendar }: {
  team: any;
  games: any[];
  selectedGameId: string | null;
  onSelect: (game: any) => void;
  onCalendar: () => void;
}) => {
  const railRef = useRef<HTMLDivElement>(null);
  const chipRefs = useRef<Map<string, HTMLButtonElement>>(new Map());

  const chips = useMemo(() => {
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);

    const live = games.filter(isGameLive);

    const upcoming = games
      .filter((g: any) => g.status === 'scheduled' && !isGameLive(g) && new Date(g.date) >= startOfToday)
      .sort((a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime())
      .slice(0, live.length > 0 ? 1 : 2); // next game + one beyond

    // 4 chips max: fill what's left with the most recent results
    const priorCount = Math.max(1, 4 - live.length - upcoming.length);
    const completed = games
      .filter((g: any) => g.status === 'completed')
      .sort((a: any, b: any) => new Date(b.date).getTime() - new Date(a.date).getTime())
      .slice(0, Math.min(2, priorCount));

    const seen = new Set<string>();
    return [...completed, ...live, ...upcoming]
      .filter((g: any) => (seen.has(g.id) ? false : (seen.add(g.id), true)))
      .sort((a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime())
      .slice(0, 4);
  }, [games]);

  // Keep the selected chip in view (scrollLeft math — scrollIntoView can scroll the page)
  useEffect(() => {
    if (!selectedGameId) return;
    const rail = railRef.current;
    const chip = chipRefs.current.get(selectedGameId);
    if (rail && chip) {
      const target = chip.offsetLeft - 12;
      if (target < rail.scrollLeft || chip.offsetLeft + chip.offsetWidth > rail.scrollLeft + rail.clientWidth) {
        rail.scrollLeft = Math.max(0, target);
      }
    }
  }, [selectedGameId, chips.length]);

  if (chips.length === 0) return null;

  return (
    <div style={{ display: 'flex', alignItems: 'center', backgroundColor: DS.bg, borderBottom: hairline, padding: '8px 0' }}>

      {/* Chip rail */}
      <div
        ref={railRef}
        style={{ display: 'flex', gap: '8px', overflowX: 'auto', flex: 1, padding: '4px 8px 4px 16px', scrollbarWidth: 'none', justifyContent: 'safe center' }}
        className="no-scrollbar"
      >
        {chips.map((game: any) => {
          const selected = game.id === selectedGameId;
          const live = isGameLive(game);
          const d = new Date(game.date);
          const isCompleted = game.status === 'completed';
          const won = isCompleted && game.result?.outcome === 'W';
          const lost = isCompleted && game.result?.outcome === 'L';

          return (
            <button
              key={game.id}
              ref={el => { if (el) chipRefs.current.set(game.id, el); }}
              onClick={() => onSelect(game)}
              style={{
                flexShrink: 0,
                minWidth: '92px',
                padding: '12px 12px',
                borderRadius: '10px',
                border: selected ? `1.5px solid ${DS.textPri}` : `0.5px solid ${DS.sep}`,
                background: 'none',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '3px',
              }}
            >
              {/* Weekday + day */}
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '5px' }}>
                <span style={{ fontSize: '12px', fontWeight: 400, color: DS.textMuted, textTransform: 'uppercase' }}>
                  {format(d, 'EEE')}
                </span>
                <span style={{ fontSize: '19px', fontWeight: 600, color: DS.textPri }}>
                  {format(d, 'd')}
                </span>
              </div>
              {/* Opponent */}
              <span style={{ fontSize: '14px', fontWeight: 600, color: DS.textPri, whiteSpace: 'nowrap' }}>
                {game.isHome ? '' : '@'}{abbreviateOpponent(game.opponent)}
              </span>
              {/* Result / time / live */}
              {live ? (
                <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: GS.green, display: 'inline-block' }} />
                  <span style={{ fontSize: '12px', fontWeight: 600, color: DS.textPri, letterSpacing: '0.04em' }}>LIVE</span>
                </span>
              ) : isCompleted && game.result ? (
                <span style={{ fontSize: '12px', fontWeight: 600, color: lost ? DS.red : DS.textPri }}>
                  {game.result.outcome} {game.result.myScore}-{game.result.opponentScore}
                </span>
              ) : (
                <span style={{ fontSize: '12px', fontWeight: 400, color: DS.textSec }}>
                  {game.time || format(d, 'h:mma')}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Calendar */}
      <button
        onClick={onCalendar}
        aria-label="Full schedule"
        style={{ padding: '0 16px 0 10px', background: 'none', border: 'none', cursor: 'pointer', flexShrink: 0, display: 'flex' }}
      >
        <Calendar style={{ width: '20px', height: '20px', color: DS.textSec }} />
      </button>
    </div>
  );
};
